import { FC, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Muuri from 'muuri';
import { Header, ScrollView } from '$uikit';
import { useModalClose, useTranslator } from '$hooks';
import { goBack, showModal } from '$navigation/helper';
import './AdventCalendar.sass';
import { RootState } from '$store/rootReducer';
import {
  adventCalendarSelector,
  adventCalendarLoadingSelector,
  adventCalendarActions,
  AdventCalendarDay,
} from '$store/adventCalendar';
import { ReactComponent as CheckedIcon } from '$assets/24/done_24_green.svg';
import { Currency, Loader } from '$shared/components';
import { IcClock28 } from '$assets';
import topIcon from './assets/top-icon.png';
import bottomIcon from './assets/botton-icon.png';
import bg1 from './assets/1.png';
import bg2 from './assets/2.png';
import bg3 from './assets/3.png';
import bg4 from './assets/4.png';
import bg5 from './assets/5.png';
import bg6 from './assets/6.png';
import bg7 from './assets/7.png';
import bg8 from './assets/8.png';
import bg9 from './assets/9.png';
import bg10 from './assets/10.png';
import bg11 from './assets/11.png';
import bg12 from './assets/12.png';
import bg13 from './assets/13.png';
import bg14 from './assets/14.png';


interface IAdventCalendarProps {
  userId: number;
}

export const AdventCalendar: FC<IAdventCalendarProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const gridRef = useRef<HTMLDivElement>(null);
  const muuriRef = useRef<Muuri | null>(null);

  const adventCalendar = useSelector((state: RootState) =>
    adventCalendarSelector(state, userId),
  );
  const loading = useSelector((state: RootState) =>
    adventCalendarLoadingSelector(state, userId),
  );

  useEffect(() => {
    if (userId) {
      dispatch(adventCalendarActions.getAdventCalendar(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (gridRef.current && !loading && adventCalendar) {
      muuriRef.current = new Muuri(gridRef.current, {
        items: '.AdventCalendar__day',
        layoutDuration: 400,
        layoutEasing: 'ease',
        dragEnabled: false,
        layout: {
          fillGaps: true,
          horizontal: false,
          alignRight: false,
          alignBottom: false,
          rounding: false,
        },
        sortData: {
          customOrder: (_item: any, element: HTMLElement) => {
            const index = parseInt(element.getAttribute('data-index') || '0', 10);
            if (index === 7) return 8;
            if (index === 8) return 7;
            return index;
          },
        },
      });

      muuriRef.current.sort('customOrder');

      const handleResize = () => {
        if (muuriRef.current) {
          muuriRef.current.refreshItems();
          muuriRef.current.layout();
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (muuriRef.current) {
          muuriRef.current.destroy();
          muuriRef.current = null;
        }
      };
    }
  }, [loading, adventCalendar]);

  const handleClose = () => {
    goBack();
    return false;
  };

  useModalClose('adventcalendar' + userId, () => {
    return handleClose();
  });

  const days = adventCalendar?.days || [];

  const lastUnlockedIndex = days.reduce((lastIndex, day, idx) => {
    return day?.isUnlocked ? idx : lastIndex;
  }, -1);

  const groups = [
    { items: days.slice(0, 3), startIndex: 0, useMuuri: false },
    { items: days.slice(3, 9), startIndex: 3, useMuuri: true },
    { items: days.slice(9, 12), startIndex: 9, useMuuri: false },
    { items: days.slice(12, 14), startIndex: 12, useMuuri: false },
  ];

  const handleDayClick = (dayData: AdventCalendarDay | null, index: number) => {
    if (!dayData || !dayData.bundleId) return;

    if (dayData.hasAccess || dayData.isUnlocked || index === 0) {
      showModal('bundle' + dayData.bundleId);
    }
  };

  const getDayStatus = (dayData: AdventCalendarDay | null, index: number) => {
    if (!dayData || !dayData.bundleId) return 'empty';

    if (dayData.hasAccess) return 'unlocked';

    if (index === 0) return 'available';

    if (dayData.isUnlocked) return 'available';

    if (index === lastUnlockedIndex + 1) return 'tomorrow';

    return 'locked';
  };

  const getPreviewImage = (dayData: AdventCalendarDay | null) => {
    if (!dayData || !dayData.previews || dayData.previews.length === 0) {
      return null;
    }

    const preview = dayData.previews[0];
    const coverUrl = preview?.photo?.photoMedium;
    const thumbnail = preview?.photo?.thumbnail;

    return coverUrl || thumbnail || null;
  };

  const getDayImage = (index: number): string => {
    const images: { [key: number]: string } = {
      0: bg1,
      1: bg2,
      2: bg3,
      3: bg4,
      4: bg5,
      5: bg6,
      6: bg7,
      7: bg8,
      8: bg9,
      9: bg10,
      10: bg11,
      11: bg12,
      12: bg13,
      13: bg14,
    };

    return images[index] || bg1;
  };

  const getDaySize = (index: number): string => {
    const sizes: { [key: number]: string } = {
      0: '104x104',
      1: '104x104',
      2: '104x104',
      3: '136x128',
      4: '88x152',
      5: '88x104',
      6: '136x128',
      7: '88x104',
      8: '88x152',
      9: '104x104',
      10: '104x104',
      11: '104x104',
      12: '84x136',
      13: '144x136',
    };

    return sizes[index] || '104x104';
  };

  if (!loading && !adventCalendar) return null;

  return (
    <div className="AdventCalendar">
      <Header showCloseButton>{t('advent_calendar_title')}</Header>
      <ScrollView>
        {loading && !adventCalendar ? (
          <Loader isWrapped />
        ) : (
          <>
            <div className="AdventCalendar__banner">
              <img
                src={topIcon}
                alt=""
                className="AdventCalendar__banner-icon AdventCalendar__banner-icon--top"
              />
              <div className="AdventCalendar__banner-content">
                <h2 className="AdventCalendar__banner-title">
                  {t(adventCalendar?.title || '') || t('advent_calendar_title')}
                </h2>
              </div>
              <img
                src={bottomIcon}
                alt=""
                className="AdventCalendar__banner-icon AdventCalendar__banner-icon--bottom"
              />
            </div>

            <div className="AdventCalendar__grid_wrapper">
              {groups.map((group, groupIdx) => (
                <div
                  key={groupIdx}
                  ref={group.useMuuri ? gridRef : null}
                  className={`AdventCalendar__grid AdventCalendar__grid--group${
                    groupIdx + 1
                  }`}
                >
                  {group.items.map((dayData, itemIdx) => {
                    const index = group.startIndex + itemIdx;
                    const status = getDayStatus(dayData, index);
                    const previewImage = getPreviewImage(dayData);
                    const sizeClass = getDaySize(index);

                    return (
                      <div
                        key={index}
                        data-index={index}
                        className={`AdventCalendar__day AdventCalendar__day--${status}${
                          sizeClass ? ` AdventCalendar__day--size-${sizeClass}` : ''
                        }`}
                        onClick={() => handleDayClick(dayData, index)}
                        style={
                          previewImage && dayData?.hasAccess
                            ? { backgroundImage: `url(${previewImage})` }
                            : undefined
                        }
                      >
                        {status !== 'unlocked' && (
                          <img
                            alt=""
                            src={getDayImage(index)}
                            className={`AdventCalendar__day--bg_${sizeClass}`}
                          />
                        )}
                        {status === 'tomorrow' && (
                          <div className="AdventCalendar__day-badge">
                            <IcClock28 /> Tomorow
                          </div>
                        )}
                        {status === 'available' && dayData && (
                          <div className="AdventCalendar__day-badge">
                            {dayData.price === 0 ? (
                              t('advent_free')
                            ) : (
                              <Currency
                                amount={dayData.price ?? 0}
                                currency={dayData.currency || 'RUB'}
                              />
                            )}
                          </div>
                        )}
                        {status === 'unlocked' && (
                          <div className="AdventCalendar__day-unlocked-badge">
                            <CheckedIcon />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </ScrollView>
    </div>
  );
};
